import React, {useEffect, useMemo, useState} from "react";
import {Breadcrumbs, Card, CardContent, Link, Tab, tabClasses, TabList, TabPanel, Tabs, Skeleton} from "@mui/joy";
import Box from "@mui/joy/Box";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import Typography from "@mui/joy/Typography";
import {GetAllCourses} from "../../axios/Axios";
import Grid from "@mui/joy/Grid";
import {AspectRatio} from "@mui/joy";
import {LockRounded} from "@mui/icons-material";
import Tooltip from "@mui/joy/Tooltip";

interface Prerequisite {
    slug: string;
    title: string;
}

interface Course {
    slug: string;
    title: string;
    description: string;
    thumbnail: string;
    is_locked: boolean;
    prerequisite: Prerequisite | null;
    type: string; // Add the 'type' property (e.g., 'sales', 'franchise')
}

// Reusable component to render the grid of courses
// This avoids code duplication between the tabbed and non-tabbed views
const CourseGrid = ({courses}: { courses: Course[] }) => {
    if (!courses || courses.length === 0) {
        return (
            <Box
                sx={{
                    textAlign: 'center',
                    py: 8,
                    px: 2
                }}
            >
                <Typography level="h4" sx={{mb: 1}}>
                    No courses available
                </Typography>
                <Typography level="body-md" color="neutral">
                    Check back soon for new learning materials for this category.
                </Typography>
            </Box>
        );
    }

    return (
        <Grid container spacing={3}>
            {courses.map((course) => (
                <Grid xs={12} sm={6} md={4} lg={3} key={course.slug}>
                    <Tooltip
                        arrow
                        placement="top"
                        variant="outlined"
                        title={
                            course.is_locked
                                ? `Please complete "${course.prerequisite?.title}" first`
                                : ''
                        }
                    >
                        <Box>
                            <Card
                                variant="outlined"
                                sx={{
                                    height: '100%',
                                    transition: 'all 0.3s ease',
                                    ...(course.is_locked && {
                                        opacity: 0.6,
                                        pointerEvents: 'none',
                                    }),
                                    '&:hover': !course.is_locked && {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 'lg',
                                        borderColor: 'primary.outlinedBorder',
                                    },
                                }}
                            >
                                <AspectRatio ratio="16/9" sx={{borderRadius: 'sm'}}>
                                    <img
                                        src={course.thumbnail}
                                        alt={course.title}
                                        loading="lazy"
                                        style={{
                                            objectFit: 'cover',
                                        }}
                                    />
                                    {course.is_locked && (
                                        <Box sx={{
                                            position: 'absolute', top: 8, right: 8,
                                            backgroundColor: 'rgba(0,0,0,0.5)',
                                            borderRadius: '50%',
                                            p: 0.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <LockRounded sx={{color: 'white'}}/>
                                        </Box>
                                    )}
                                </AspectRatio>
                                <CardContent>
                                    <Link
                                        overlay
                                        underline="none"
                                        href={course.is_locked ? undefined : `/dashboard/knowledge-base/${course.slug}`}
                                        sx={{
                                            cursor: course.is_locked ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        <Typography
                                            level="title-lg"
                                            sx={{
                                                mb: 1,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {course.title}
                                        </Typography>
                                    </Link>
                                    <Typography
                                        level="body-sm"
                                        color="neutral"
                                        sx={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {course.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    </Tooltip>
                </Grid>
            ))}
        </Grid>
    );
};

export default function KnowledgeBase() {
    const [index, setIndex] = React.useState(0);
    const [loadingData, setLoadingData] = useState(true);
    const [courses, setCourses] = React.useState<Course[]>([]); // Initialize with empty array

    useEffect(() => {
        setLoadingData(true)

        const getCourses = async () => {
            try {
                const coursesData = await GetAllCourses();
                setCourses(coursesData);
            } catch (error) {
                console.error("Failed to fetch courses:", error);
            } finally {
                setLoadingData(false);
            }
        };

        getCourses();

    }, [])

    const sortedCourses = useMemo(() => {
        if (!courses || courses.length === 0) {
            return [];
        }
        const sorted: Course[] = [];
        const courseMap = new Map(courses.map(course => [course.slug, course]));
        const processedSlugs = new Set<string>();
        const visitingSlugs = new Set<string>();

        const visit = (course: Course) => {
            if (processedSlugs.has(course.slug)) {
                return;
            }
            if (visitingSlugs.has(course.slug)) {
                console.error(`Cyclical dependency detected involving course: ${course.title}`);
                return;
            }
            visitingSlugs.add(course.slug);
            if (course.prerequisite) {
                const prereq = courseMap.get(course.prerequisite.slug);
                if (prereq) {
                    visit(prereq);
                } else {
                    console.warn(`Prerequisite "${course.prerequisite.slug}" for course "${course.title}" not found.`);
                }
            }
            visitingSlugs.delete(course.slug);
            if (!processedSlugs.has(course.slug)) {
                processedSlugs.add(course.slug);
                sorted.push(course);
            }
        };

        for (const course of courses) {
            visit(course);
        }

        return sorted;
    }, [courses]);

    // CHANGE HERE: Get unique course types
    const courseTypes = useMemo(() => {
        if (!courses || courses.length === 0) return [];
        // Use a Set to get unique types, then sort for a consistent tab order
        return Array.from(new Set(courses.map((c) => c.type))).sort();
    }, [courses]);

    // CHANGE HERE: Group sorted courses by type for easy access in tab panels
    const coursesByType = useMemo(() => {
        const grouped: { [key: string]: Course[] } = {};
        for (const course of sortedCourses) {
            if (!grouped[course.type]) {
                grouped[course.type] = [];
            }
            grouped[course.type].push(course);
        }
        return grouped;
    }, [sortedCourses]);


    const renderContent = () => {
        if (loadingData) {
            return (
                <Grid container spacing={3}>
                    {[1, 2, 3, 4].map((item) => (
                        <Grid xs={12} sm={6} md={4} lg={3} key={item}>
                            <Card variant="outlined" sx={{height: '100%'}}>
                                <AspectRatio ratio="16/9">
                                    <Skeleton variant="overlay"/>
                                </AspectRatio>
                                <CardContent>
                                    <Skeleton variant="text" level="h4" width="80%"/>
                                    <Skeleton variant="text" level="body-sm" width="100%"/>
                                    <Skeleton variant="text" level="body-sm" width="90%"/>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )
        }

        // CHANGE HERE: Conditional rendering based on the number of types
        if (courseTypes.length > 1) {
            // RENDER TABS if more than one course type exists
            return (
                <Tabs
                    aria-label="Knowledge Base Tabs"
                    value={index}
                    onChange={(event, value) => setIndex(value as number)}
                >
                    <TabList
                        sx={{
                            mb: 3,
                            [`&& .${tabClasses.root}`]: {
                                flex: 'initial',
                                bgcolor: 'transparent',
                                fontWeight: 600,
                                '&:hover': {bgcolor: 'transparent'},
                                [`&.${tabClasses.selected}`]: {
                                    color: 'primary.plainColor',
                                    '&::after': {
                                        height: 2,
                                        borderTopLeftRadius: 3,
                                        borderTopRightRadius: 3,
                                        bgcolor: 'primary.500',
                                    },
                                },
                            },
                        }}
                    >
                        {courseTypes.map((type) => (
                            <Tab indicatorInset key={type}>
                                {/* Capitalize first letter for display */}
                                {type.charAt(0).toUpperCase() + type.slice(1)} ({coursesByType[type]?.length || 0})
                            </Tab>
                        ))}
                    </TabList>

                    {courseTypes.map((type, i) => (
                        <TabPanel value={i} key={type} sx={{p: 0}}>
                            <CourseGrid courses={coursesByType[type] || []}/>
                        </TabPanel>
                    ))}
                </Tabs>
            );
        } else {
            // RENDER A SINGLE GRID if there's only one type (or none)
            return <CourseGrid courses={sortedCourses}/>;
        }
    };


    return (
        <>
            <Box sx={{display: 'flex', mb: 2}}>
                <Breadcrumbs
                    size="sm"
                    aria-label="breadcrumbs"
                    separator={<ChevronRightRoundedIcon fontSize="small"/>}
                    sx={{pl: 0}}
                >
                    <Link
                        underline="none"
                        color="neutral"
                        href="/dashboard"
                        aria-label="Home"
                    >
                        <HomeRoundedIcon/>
                    </Link>
                    <Link
                        underline="hover"
                        color="neutral"
                        href="/dashboard"
                        fontSize={12}
                        fontWeight={500}
                    >
                        Dashboard
                    </Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>
                        Knowledge Base
                    </Typography>
                </Breadcrumbs>
            </Box>

            <Box sx={{mb: 4}}>
                <Typography level="h2" component="h1" sx={{mb: 1}}>
                    Knowledge Base
                </Typography>
                <Typography level="body-md" color="neutral">
                    Explore our comprehensive collection of courses and learning materials
                </Typography>
            </Box>

            <Box sx={{flexGrow: 1, overflowX: 'hidden'}}>
                {renderContent()}
            </Box>
        </>
    )
}